import { lazy } from 'react';
import googleLogo from '@/components/PlatformIcon/icons/google.png';
import {
  EventExportPlatform,
  IntegrationType,
  PlatformTypes,
} from '@/types/gql.generated';
import { useAlwaysAvailable } from '../addToCalendar/destinations/availability.lib';
import type { DestinationModule } from '../addToCalendar/destinations/types';
import {
  getSortedCalendars,
  isSuitableIntegration,
  selectGoogleIntegrations,
} from './calendar.lib';
import { useExportModalLaunch } from './hooks/useExportModalLaunch';
import { LAUNCH_KEY_GOOGLE_MULTI } from './util.lib';

// Modal/form bodies mount only when their flow opens, so they load lazily and
// the entry stays cheap for data-only consumers (copy, icons, status).
const GoogleExportModal = lazy(() =>
  import('./components/ExportModal/ExportModal').then((module) => ({
    default: module.GoogleExportModal,
  }))
);

const GoogleCalendarPicker = lazy(() =>
  import('./components/GoogleCalendarPicker').then((module) => ({
    default: module.GoogleCalendarPicker,
  }))
);

const GoogleExportForm = lazy(() =>
  import('./components/GoogleExportForm').then((module) => ({
    default: module.GoogleExportForm,
  }))
);

export const googleIntegrationDestination: DestinationModule = {
  platform: PlatformTypes.GoogleIntegration,
  behavior: 'sync',
  name: 'Google',
  addLabel: 'Add to Google',
  description:
    'Any changes to this calendar will update your Google Calendar within 15 minutes. You can subscribe to the entire calendar, or individual events.',
  syncKind: 'sync',
  icon: { kind: 'image', src: googleLogo },
  isPinnable: true,
  persistedByServer: true,
  statusSource: 'integrationExports',
  useIsAvailable: useAlwaysAvailable,
  ExportForm: GoogleExportForm,
  integration: {
    exportPlatform: EventExportPlatform.Google,
    adapter: {
      integrationType: IntegrationType.GoogleCalendar,
      select: selectGoogleIntegrations,
      getSortedCalendars,
      isSuitableIntegration,
    },
    launchPrefix: 'google-add',
    multiLaunchKey: LAUNCH_KEY_GOOGLE_MULTI,
    ExportModal: GoogleExportModal,
    CalendarPicker: GoogleCalendarPicker,
    supportsSubscription: true,
    useLaunchResume: useExportModalLaunch,
    // Google-only quirks, preserved and declared: the magic Activity drawer
    // reopens after the modal workflow, the IntegrationErrors banner renders in
    // its modal/drawer, useCurrentDestination auto-defaults to its primary
    // calendar, and magic share pages export through the legacy quick-entries
    // mutation.
    hasModalWorkflowResume: true,
    hasErrorsBanner: true,
    isAutoDefault: true,
    usesLegacyMagicExport: true,
  },
};
